import { useLoggedInUserId } from 'modules/Auth/hooks';
import { useDataCollection } from 'modules/AnalyticsCollection/AnalyticsCollectionProvider';
import { useFeatureFlagsLD } from 'modules/FeatureFlagsLD/FeatureFlagsProviderLD';
import { useBreakpoint } from 'utils/responsive';
import { breakpoints } from 'styles/utils';
import { IconProps } from 'components/Icons/IconProps';
import { IconBuilding, IconCirclesFour, IconDiscord, IconDocs, IconMessageSquare, IconServer04, IconKey } from 'components/Icons';
import { API_STATUS_LINK, CHANGELOG_LINK, CONTACT_US_LINK, DISCORD_LINK, QUICK_START_GUIDE_LINK } from 'constants/externalLinks';

interface AccountLink {
  text: string;
  url?: string;
  badge?: string;
  externalLink?: boolean;
  handleClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  icon?: React.FC<IconProps>;
  rel?: string;
  'data-testid'?: string;
}

export interface AccountLinkGroup {
  id: string;
  title?: string;
  links: AccountLink[];
}

export function useAccountPopoverLinks(): AccountLinkGroup[] {
  const userId = useLoggedInUserId();
  const { track } = useDataCollection();
  const { changelog } = useFeatureFlagsLD();

  const onHelpMenuClick = (btnText: string): void => {
    track('Help Menu Clicked', { param1: 'main_header', param2: btnText });
  };

  if (!userId) {
    return [];
  }

  const helpLinks: AccountLink[] = [
    {
      text: 'Contact Us',
      url: CONTACT_US_LINK,
      externalLink: true,
      rel: 'noreferrer',
      icon: IconMessageSquare,
      handleClick: () => onHelpMenuClick('Contact Us'),
    },
    {
      text: 'Quick Start Guide',
      url: QUICK_START_GUIDE_LINK,
      externalLink: true,
      rel: 'noreferrer',
      icon: IconDocs,
      handleClick: () => onHelpMenuClick('Create Guide'),
    },
    {
      text: 'API Status',
      url: API_STATUS_LINK,
      externalLink: true,
      rel: 'noreferrer',
      icon: IconServer04,
      handleClick: () => onHelpMenuClick('API Status'),
    },
    {
      text: 'Join our Discord Channel',
      url: DISCORD_LINK,
      externalLink: true,
      rel: 'noreferrer',
      icon: IconDiscord,
      'data-testid': 'join-discord',
      handleClick: () => onHelpMenuClick('Join our Discord Channel'),
    },
    // {
    //   text: 'Product Roadmap',
    //   url: PRODUCT_BOARD_LINK,
    //   externalLink: true,
    //   rel: 'noreferrer',
    //   icon: IconSettingsDashboard,
    //   'data-testid': 'product-roadmap',
    //   handleClick: () => onHelpMenuClick('Product Roadmap'),
    // },
  ];

  const groups: AccountLinkGroup[] = [
    {
      id: '0',
      title: '',
      links: [
        {
          text: 'Manage Organizations',
          url: `/${userId}/settings/organizations`,
          icon: IconBuilding,
        },
        {
          text: 'Collaborations',
          url: `/${userId}/collaborations`,
          icon: IconCirclesFour,
        },
        {
          text: 'Secrets',
          url: `/${userId}/settings/secrets`,
          icon: IconKey,
        },
      ],
    },
    {
      id: '1',
      title: 'Help',
      links: helpLinks,
    },
  ];

  // Add Latest Release Notes section if feature flag is enabled
  if (changelog) {
    groups.push({
      id: '2',
      title: '',
      links: [
        {
          text: 'Latest Release Notes',
          url: CHANGELOG_LINK,
          externalLink: true,
          icon: IconDocs,
          rel: 'noreferrer',
          handleClick: () => onHelpMenuClick('Latest Release Notes'),
        },
      ],
    });
  }

  return groups;
}

export function useSignOutWithAnalytics() {
  const { track } = useDataCollection();
  const isTabletViewport = useBreakpoint(breakpoints.down('bigtab'));
  const viewport = isTabletViewport ? 'mobile' : 'desktop';

  return {
    trackSignOut: () => {
      track('Signout Button Clicked', { source: 'navbar menu', viewport });
    },
  };
}
